// Render-helper för regionsidor (/filmproduktion-<region>).
// Följer samma struktur som /filmproduktion-mellansverige.

import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";
import type { RegionMeta } from "@/lib/seo/regions";

function faqFor(region: RegionMeta) {
  return [
    {
      q: `Vilka områden räknas till ${region.name}?`,
      a: `${region.areas.join(", ")}. Fenny producerar regelbundet i hela området.`,
    },
    {
      q: `Varifrån bemannar ni produktioner i ${region.name}?`,
      a: `${region.servedFrom}. Restid inom regionen ingår transparent i offerten — inga dolda påslag.`,
    },
    {
      q: `Vad kostar filmproduktion i ${region.name}?`,
      a: "Samma prislogik som i Stockholm, utan ortspåslag. Social-content från 25 000 kr, företagsfilm och kundcase 60 000–160 000 kr, reklamfilm från 150 000 kr. Pulse-abonnemang från 14 995 kr/mån.",
    },
    {
      q: `Hur snabbt är ni på plats i ${region.name}?`,
      a: region.leadTime,
    },
    {
      q: `Kan ni filma på flera orter i ${region.name} samma vecka?`,
      a: "Ja. Vi blockar inspelningsdagar geografiskt så flera orter klaras av i samma resa — det sänker både kostnad och kalendertid.",
    },
  ];
}

export function regionRoute(region: RegionMeta) {
  const path = `/filmproduktion-${region.slug}`;
  const absUrl = `https://fenny.se${path}`;
  const faq = faqFor(region);
  const base = `Filmproduktion ${region.name} — utan reseavgifter | Fenny`;
  const title = base.length <= 60 ? base : `Filmproduktion ${region.name} | Fenny`;
  const description = `Filmproduktion i ${region.name}: reklamfilm, företagsfilm, kundcase och employer branding. Teamet bemannas från ${region.servedFrom} — möte inom 5 arbetsdagar och inga reseavgifter inom länet.`;

  return {
    head: () => ({
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: `Filmproduktion ${region.name} | Fenny` },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: absUrl },
      ],
      links: [{ rel: "canonical", href: absUrl }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path, name: title, description })) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            serviceSchema({
              name: `Filmproduktion ${region.name}`,
              path: path,
              description: `Fenny levererar filmproduktion i ${region.name} från ${region.servedFrom}. Reklamfilm, företagsfilm, employer branding och kundcase.`,
              serviceType: "Filmproduktion",
              priceRange: "Från 14 995 kr/mån (Pulse) eller 25 000 kr/projekt",
              areaServed: region.areas,
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqPage(faq)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: "Hem", path: "/" },
              { name: "Filmproduktion", path: "/filmproduktion" },
              { name: region.name, path },
            ]),
          ),
        },
      ],
    }),
    component: function RegionHub() {
      return (
        <>
          <Section>
            <div className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {region.name}
            </div>
            <h1 className="text-balance text-4xl font-semibold leading-[1.05] md:text-6xl">
              Filmproduktion i {region.name} — {region.headline}
            </h1>
            <QuickAnswer text={region.quickAnswer} />
          </Section>

          <Section>
            <h2 className="text-3xl font-semibold md:text-4xl">
              Orter i {region.name}
            </h2>
            <ul className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {region.cities.map((c) => (
                <li key={c.path}>
                  <Link
                    to={c.path}
                    className="group flex items-center justify-between rounded-xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 text-sm transition hover:border-foreground/30 hover:bg-foreground/[0.06]"
                  >
                    <span>{c.label}</span>
                    <ArrowRight className="size-4 opacity-50 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </Section>

          <Section>
            <h2 className="text-3xl font-semibold md:text-4xl">Vanliga frågor</h2>
            <div className="mt-8 max-w-3xl space-y-6">
              {faq.map((f) => (
                <div key={f.q}>
                  <h3 className="text-lg font-medium">{f.q}</h3>
                  <p className="mt-2 text-foreground/70">{f.a}</p>
                </div>
              ))}
            </div>
          </Section>

          <SignatureCta />
        </>
      );
    },
  };
}
